import './PhotosSection.scss';

import React, { useCallback } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import Slider from 'react-slick';
import { useIsEmbedded } from 'hooks/useIsEmbedded';
import PropTypes from 'prop-types';

import {
  getMinTierNameForMobileGpsPhotosFeatures,
  getTimecardPhotosEnabled,
} from 'selectors/session';

import GoToManagePlanPageButton from 'features/managePlan/components/GoToManagePlanPageButton';
import Section from 'features/timesheets/TimesheetsPage/TimecardModal/Section';

import { cxHelpers } from 'util/className';
import { formatDateToMsgTime } from 'util/dateTime';
import {
  EVENT_ACTIONS,
  EVENT_CATEGORIES,
  PRODUCT_AREAS,
} from 'util/tracking_constants';

import { getGpsPhotosEnabled } from '../selectors';
const { cx, cxEl } = cxHelpers('PhotosSection');

import Box from 'fe-design-base/atoms/Box';
import Text from 'fe-design-base/atoms/Text';

import { toI18n } from 'util/i18n';
import { trackUxEvent } from 'util/tracking';

const PhotosSection = ({
  isMobile,
  photos,
  gpsPhotosEnabled,
  timecardPhotosEnabled,
  tierName,
}) => {
  const isEmbedded = useIsEmbedded();

  const handleClickUpgrade = () => {
    trackUxEvent({
      productArea: PRODUCT_AREAS.TIMESHEETS,
      eventCategory: EVENT_CATEGORIES.TIMECARD_DETAIL,
      eventAction:
        EVENT_ACTIONS.UPGRADE_BUTTON_FROM_PHOTO_GPS_PAYWALL_CLICKED_V3,
    });
  };

  const handleClick = useCallback(
    item => () => {
      const url = item.get('url');
      const isGpsPhotoAndEnabled = item.get('gps_photo') && gpsPhotosEnabled;
      const isClickable = !!url && isGpsPhotoAndEnabled;
      if (isClickable) {
        window.open(url, '_blank');
      }
    },
    [gpsPhotosEnabled]
  );

  function gpsPhotoPaywall() {
    return (
      <Box>
        <Box tcenter>
          <Text
            fw600
            fs18
            navy
            i18n="timecard_modal.photo_carousel.geofence_clock_in.title"
          />
          <Box mv={16}>
            <Text
              fs16
              navy
              i18n="timecard_modal.photo_carousel.geofence_clock_in.text"
              i18nProps={{ tier: tierName }}
            />
          </Box>
          <GoToManagePlanPageButton
            fullWidth
            loadFullPage
            size="large"
            theme={isEmbedded ? 'clover-green' : 'primary-purple'}
            onClick={handleClickUpgrade}
            i18nProps={{ tier: tierName }}
            i18n={
              isEmbedded
                ? 'manage_plans.title'
                : 'timecard_modal.photo_carousel.geofence_clock_in.button'
            }
          />
        </Box>
      </Box>
    );
  }

  function renderSlides() {
    return photos.map((item, index) => {
      const isGpsPhotoButDisabled = item.get('gps_photo') && !gpsPhotosEnabled;
      const isGpsPhotoAndEnabled = item.get('gps_photo') && gpsPhotosEnabled;
      const url = item.get('url');
      const isClickable = !!url && isGpsPhotoAndEnabled;

      const showPhotoTimes =
        isGpsPhotoAndEnabled ||
        (!item.get('gps_photo') && timecardPhotosEnabled);

      const shouldBlurPhoto =
        isGpsPhotoButDisabled ||
        (!item.get('gps_photo') && !timecardPhotosEnabled);

      return (
        <Box key={index}>
          <Box
            className={cxEl('photo', {
              clickable: isClickable,
              blur: shouldBlurPhoto,
            })}
            bradiusm
            bw={1}
            block
            bcolor="mono400"
            relative
            style={{ overflow: 'hidden' }}
          >
            <img
              onClick={handleClick(item)}
              style={{ height: 210, width: 210 }}
              src={item.get('photo')}
              alt={item.get('type')}
            />
          </Box>

          {showPhotoTimes && (
            <Box tcenter mt={11} className={cxEl('times')}>
              <Text variant="captions" inline color="mono700">
                {item.get('type')}:{' '}
              </Text>
              <Text variant="captionBold" inline color="mono700">
                {formatDateToMsgTime(item.get('datetime'))}
              </Text>
              {item.get('changed_datetime') && (
                <Box tcenter mt4>
                  <Text
                    variant="captions"
                    i18n="timecard_modal.photo_carousel.time_changed"
                    inline
                    color="mono700"
                  />
                  <Text variant="captionBold" inline color="mono700">
                    {formatDateToMsgTime(item.get('changed_datetime'))}
                  </Text>
                </Box>
              )}
            </Box>
          )}
        </Box>
      );
    });
  }

  function photosList() {
    if (photos.size === 0) {
      return null;
    }

    return (
      <Box mt={16} relative>
        <Slider
          infinite={false}
          className={cxEl('timecard-slider')}
          speed={500}
          slidesToShow={1}
          slidesToScroll={1}
          arrows={!isMobile}
          variableWidth
        >
          {renderSlides()}
        </Slider>

        {!gpsPhotosEnabled && gpsPhotoPaywall()}
      </Box>
    );
  }

  return (
    <Box className={cx()}>
      <Section
        title={toI18n('timecard_modal_v3.photos_section.title', {
          props: { count: photos.size },
        })}
        iconName="Location"
        expandedContent={photosList()}
        bottomBorder
        bottomPadding
      />
    </Box>
  );
};

PhotosSection.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  photos: ImmutablePropTypes.list,
  gpsPhotosEnabled: PropTypes.bool.isRequired,
  timecardPhotosEnabled: PropTypes.bool.isRequired,
  tierName: PropTypes.string.isRequired,
};

export default connect((state, props) => ({
  gpsPhotosEnabled: getGpsPhotosEnabled(state, props),
  timecardPhotosEnabled: getTimecardPhotosEnabled(state, props),
  tierName: getMinTierNameForMobileGpsPhotosFeatures(state, props),
}))(PhotosSection);
